"use client";
import * as React from 'react';
import type { User } from '@/types/user';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  setAuth: (user: User, token: string) => void;
  clear: () => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>({ user: null, token: null, loading: true });

  // Hydrate from localStorage
  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('ace_auth') : null;
      if (raw) {
        const parsed = JSON.parse(raw) as { user: User; token: string };
        if (parsed?.token && parsed?.user) {
          setState({ user: parsed.user, token: parsed.token, loading: false });
          return;
        }
      }
    } catch {
      // swallow
    }
    setState(s => ({ ...s, loading: false }));
  }, []);

  const setAuth = React.useCallback((user: User, token: string) => {
    setState({ user, token, loading: false });
    try { window.localStorage.setItem('ace_auth', JSON.stringify({ user, token })); } catch {}
  }, []);

  const clear = React.useCallback(() => {
    setState({ user: null, token: null, loading: false });
    try { window.localStorage.removeItem('ace_auth'); } catch {}
  }, []);

  const value = React.useMemo(() => ({ ...state, setAuth, clear }), [state, setAuth, clear]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
